import React from "react";
import Button from "components/Button/Regular";
import { ReactComponent as PlusIcon } from "assets/icons/plus.svg";
import { useNavigate } from "react-router-dom";
import Page from "components/Page/Page";
import Table from "components/Table/Table";
import { PaginationBar } from "components/Pagination";
import PageSizeSelect from "components/Table/PageSizeSelect";
import { useTableView } from "components/Table/useTableView";
import { useAuth0 } from "@auth0/auth0-react";
import { useActiveOrg } from "context/ActiveOrgContext";
import { getServicesList, ServiceRow } from "services/lists.service";
import { deleteOrgService } from "services/org-services.service";
import vgToast from "utils/toast";

const ServicesList = () => {
  const navigate = useNavigate();
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const { orgId } = useActiveOrg();
  const [services, setServices] = React.useState<ServiceRow[]>([]);
  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);

  // Client-side paging and sorting: the list endpoints return whole
  // collections with no paging parameters, so there is nothing to page
  // server-side against.
  const view = useTableView(services);

  const load = React.useCallback(async () => {
    if (!isAuthenticated || orgId <= 0) {
      setServices([]);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const token = await getAccessTokenSilently();
      const { data, error: apiError } = await getServicesList(token, orgId);
      if (apiError) {
        setError(apiError.message);
        setServices([]);
        return;
      }
      setServices(data);
    } catch (e) {
      setError((e as Error).message);
      setServices([]);
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently, isAuthenticated, orgId]);

  React.useEffect(() => {
    load();
  }, [load]);

  const handleDelete = async (id: string) => {
    if (!window.confirm("Delete this service? This cannot be undone.")) return;
    if (orgId <= 0) return;
    try {
      const token = await getAccessTokenSilently();
      const { error: apiError } = await deleteOrgService(token, orgId, Number(id));
      if (apiError) {
        vgToast.error(apiError.message);
        return;
      }
      await load();
    } catch (e) {
      vgToast.error((e as Error).message);
    }
  };

  return (
    <Page
      title="Services"
      subTitle="The services you offer your clients."
      actions={
        <Button onClick={() => navigate("/app/services/new-service")}>
          <PlusIcon />
          <span>Create New Service</span>
        </Button>
      }
    >
      <PageSizeSelect
        value={view.pageSize}
        onChange={view.setPageSize}
        className="mb-3"
      />

      <Table<{
        id: string;
        name: string;
        description: string;
        configs: number;
      }>
        loading={loading}
        columns={{
          id: "Service Id",
          name: "Service Name",
          description: "Description",
          configs: "Booking Configs",
        }}
        rows={view.pageRows}
        sort={view.sort}
        onSortChange={view.toggleSort}
        emptyMessage="No services yet. Add your first service to get started."
        handleViewItem={(id) => navigate(`/app/services/${id}`)}
        handleRemoveItem={handleDelete}
      />
      {error && <div className="mt-3 text-sm text-red-500">{error}</div>}

      <div className="mt-4">
        <PaginationBar
          from={view.from}
          to={view.to}
          total={view.total}
          currentPage={view.page}
          totalPages={view.totalPages}
          onPrev={view.goPrev}
          onNext={view.goNext}
          onPageSelect={view.goToPage}
        />
      </div>
    </Page>
  );
};

export default ServicesList;
